import { Badge } from "@coval/ui/badge"
import { Card, CardContent } from "@coval/ui/card"
import { Checkbox } from "@coval/ui/checkbox"
import { Lightbulb } from "lucide-react"

import { InlineDiff } from "@/components/diff/InlineDiff"

import {
  CHANGE_TYPE_COLORS,
  CHANGE_TYPE_LABELS,
  type Change,
} from "./optimization"

interface ChangeCardProps {
  change: Change
  isSelected: boolean
  onToggleSelect: () => void
  index: number
  total: number
}

const getConfidenceLabel = (
  confidence: number,
): { label: string; variant: "success" | "warning" | "default" } => {
  if (confidence >= 0.85) {
    return { label: "High confidence", variant: "success" }
  } else if (confidence >= 0.5) {
    return { label: "Medium confidence", variant: "warning" }
  }
  return { label: "Low confidence", variant: "default" }
}

export const ChangeCard = ({
  change,
  isSelected,
  onToggleSelect,
  index,
  total,
}: ChangeCardProps) => {
  const badgeVariant = CHANGE_TYPE_COLORS[change.type]

  const confidenceInfo = change.confidence
    ? getConfidenceLabel(change.confidence)
    : null

  return (
    <Card
      className={`transition-all duration-200 ${
        isSelected
          ? "border border-coval-border bg-surface-default"
          : "border border-coval-border bg-surface-default opacity-70"
      }`}
    >
      <CardContent className="gap-3 p-4">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect()}
            aria-label={`${isSelected ? "Deselect" : "Select"} change ${index + 1}: ${change.type}`}
          />
          <span className="text-sm text-coval-text-muted">
            Change {index + 1} of {total}
          </span>
          <Badge variant={badgeVariant} size="status">
            {CHANGE_TYPE_LABELS[change.type]}
          </Badge>
          {confidenceInfo && (
            <Badge variant={confidenceInfo.variant} size="status">
              {confidenceInfo.label}
            </Badge>
          )}
        </div>

        {change.location && (
          <p className="text-xs text-coval-text-muted">
            Location: {change.location}
          </p>
        )}

        <div className="bg-surface-default rounded-lg p-3 overflow-x-auto">
          {change.originalText ? (
            <InlineDiff
              oldText={change.originalText}
              newText={change.newText}
              className="font-mono text-xs"
            />
          ) : (
            <div className="text-sm">
              <span className="text-coval-success-on-surface bg-coval-success-surface px-1 rounded">
                + {change.newText}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-start gap-2 bg-surface-default rounded-lg p-3 border border-coval-border">
          <Lightbulb
            className="w-5 h-5 text-coval-info-text shrink-0"
            aria-hidden="true"
          />
          <p className="text-sm text-coval-text-muted">{change.explanation}</p>
        </div>
      </CardContent>
    </Card>
  )
}
